import React, { useState, useEffect } from 'react';
import { userService, CreateUserData } from '../services/user.service';
import { User, UserRole } from '../types';
import UserList from '../components/user/UserList';
import UserForm from '../components/user/UserForm';
import ChangeRoleDialog from '../components/user/ChangeRoleDialog';
import ChangePasswordDialog from '../components/user/ChangePasswordDialog';
import DeleteUserDialog from '../components/user/DeleteUserDialog';

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedUserForRole, setSelectedUserForRole] = useState<User | null>(null);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState<User | null>(null);
  const [selectedUserForDelete, setSelectedUserForDelete] = useState<User | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await userService.getAllUsers();
      setUsers(data);
    } catch (err) {
      setError('Fehler beim Laden der Benutzer');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (data: CreateUserData) => {
    try {
      setActionLoading(true);
      setError(null);
      await userService.createUser(data);
      await loadUsers();
      setShowCreateForm(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Erstellen des Benutzers');
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangeRole = async (userId: number, newRole: UserRole) => {
    try {
      setActionLoading(true);
      setError(null);
      await userService.updateUserRole(userId, newRole);
      await loadUsers();
      setSelectedUserForRole(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Ändern der Rolle');
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangePassword = async (userId: number, newPassword: string) => {
    try {
      setActionLoading(true);
      setError(null);
      await userService.updateUserPassword(userId, newPassword);
      setSelectedUserForPassword(null);
      alert('Passwort erfolgreich geändert');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Ändern des Passworts');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUserForDelete) return;

    try {
      setActionLoading(true);
      setError(null);
      await userService.deleteUser(selectedUserForDelete.id);
      await loadUsers();
      setSelectedUserForDelete(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Löschen des Benutzers');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Lädt Benutzer...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Benutzerverwaltung</h1>
        <button
          onClick={() => setShowCreateForm(true)}
          className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          + Neuer Benutzer
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {showCreateForm && (
        <div className="mb-6 bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-bold mb-4">Neuen Benutzer erstellen</h2>
          <UserForm
            onSubmit={handleCreateUser}
            onCancel={() => setShowCreateForm(false)}
            isLoading={actionLoading}
          />
        </div>
      )}

      <UserList
        users={users}
        onEdit={setSelectedUserForRole}
        onDelete={setSelectedUserForDelete}
        onChangePassword={setSelectedUserForPassword}
      />

      {selectedUserForRole && (
        <ChangeRoleDialog
          user={selectedUserForRole}
          onConfirm={handleChangeRole}
          onCancel={() => setSelectedUserForRole(null)}
          isLoading={actionLoading}
        />
      )}

      {selectedUserForPassword && (
        <ChangePasswordDialog
          user={selectedUserForPassword}
          onConfirm={handleChangePassword}
          onCancel={() => setSelectedUserForPassword(null)}
          isLoading={actionLoading}
        />
      )}

      {selectedUserForDelete && (
        <DeleteUserDialog
          user={selectedUserForDelete}
          onConfirm={handleDeleteUser}
          onCancel={() => setSelectedUserForDelete(null)}
          isLoading={actionLoading}
        />
      )}
    </div>
  );
};

export default UsersPage;
